/**
 * C.13a — React node view for ```dataview``` fences.
 *
 * Reads `node.attrs.source` (the DQL text), lazily compiles it via
 * chevrotain, runs the executor against `editor.storage.dataview.notes`,
 * and renders an inline TABLE/LIST. Wikilink cells reuse the same
 * onOpen mechanism the editor pane uses (passed in via storage too).
 */

import { useEffect, useMemo, useState } from 'react';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { compileQuery } from '../../../lib/vault/dataview/dql';
import { executeQuery } from '../../../lib/vault/dataview/runner';
import type {
  Column,
  ParseError,
  RunnerCell,
  RunnerOutcome,
} from '../../../lib/vault/dataview/types';
import type { ParsedNote } from '../../../lib/vault/parse';
import { testIds } from '../../../lib/testIds';
import type { DataviewStorage } from './DataviewBlockNode';

export function DataviewBlockView(props: NodeViewProps) {
  const source = (props.node.attrs.source as string) ?? '';
  // Access storage via the typed extensionStorage path. The TipTap
  // node's addStorage() returns DataviewStorage; the VaultTipTapEditor
  // host mutates `notes` + `onOpenLink` on it whenever those change.
  const storage = (
    props.editor.extensionStorage as unknown as Record<string, unknown>
  )['dataviewBlock'] as DataviewStorage | undefined;
  const notes = storage?.notes ?? EMPTY_NOTES;
  const onOpenLink = storage?.onOpenLink;

  const [outcome, setOutcome] = useState<RunnerOutcome | null>(null);
  const [parseError, setParseError] = useState<ParseError | null>(null);
  const [pending, setPending] = useState(true);

  // Compile + execute when source or notes change. Compile is async
  // (dynamic chevrotain import); execute is synchronous.
  const trimmed = useMemo(() => source.trim(), [source]);
  useEffect(() => {
    let cancelled = false;
    setPending(true);
    setParseError(null);
    if (trimmed.length === 0) {
      setOutcome({ ok: false, message: 'Empty dataview block' });
      setPending(false);
      return;
    }
    void (async () => {
      const result = await compileQuery(trimmed);
      if (cancelled) return;
      if (!result.ok) {
        setParseError(result.error);
        setOutcome(null);
        setPending(false);
        return;
      }
      const exec = executeQuery(result.ast, notes);
      if (cancelled) return;
      setOutcome(exec);
      setPending(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [trimmed, notes]);

  return (
    <NodeViewWrapper
      as="div"
      data-testid={testIds.dataview.block(props.getPos?.() ?? 0)}
      className="dataview-block"
      style={{
        margin: '8px 0',
        border: '1px solid #ece9d8',
        background: '#fafafa',
        padding: 8,
        fontFamily: "var(--xp-font-body)",
        fontSize: 12,
        color: '#000',
      }}
    >
      {pending && (
        <div
          data-testid={testIds.dataview.loadingIndicator}
          style={{ color: '#444' }}
        >
          Loading dataview…
        </div>
      )}
      {parseError && (
        <div
          data-testid={testIds.dataview.errorBanner}
          style={{
            color: '#a00',
            background: '#fff0f0',
            border: '1px solid #d99',
            padding: 6,
          }}
        >
          dataview parse error: {parseError.message}
          {parseError.column !== undefined ? ` (col ${parseError.column})` : ''}
        </div>
      )}
      {outcome && outcome.ok && outcome.rows.length === 0 && (
        <div style={{ color: '#555', fontStyle: 'italic' }}>No matches.</div>
      )}
      {outcome && outcome.ok && outcome.rows.length > 0 && outcome.type === 'TABLE' && (
        <DataviewTable
          columns={outcome.columns}
          rows={outcome.rows}
          onOpenLink={onOpenLink}
        />
      )}
      {outcome && outcome.ok && outcome.rows.length > 0 && outcome.type === 'LIST' && (
        <DataviewList rows={outcome.rows} onOpenLink={onOpenLink} />
      )}
      {outcome && outcome.ok === false && (
        <div
          data-testid={testIds.dataview.errorBanner}
          style={{
            color: '#a00',
            background: '#fff0f0',
            border: '1px solid #d99',
            padding: 6,
          }}
        >
          dataview runtime error: {outcome.message}
        </div>
      )}
    </NodeViewWrapper>
  );
}

const EMPTY_NOTES: ParsedNote[] = [];

function DataviewTable({
  columns,
  rows,
  onOpenLink,
}: {
  columns: Column[];
  rows: { path: string; cells: Record<string, RunnerCell> }[];
  onOpenLink?: (path: string, modifier: 'pane-a' | 'pane-b') => void;
}) {
  return (
    <table
      data-testid={testIds.dataview.table}
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 12,
      }}
    >
      <thead>
        <tr>
          {columns.map((c) => (
            <th
              key={c.field}
              data-testid={testIds.dataview.headerCell(c.field)}
              style={{
                textAlign: 'left',
                borderBottom: '1px solid #888',
                padding: '3px 6px',
                background: '#e0e0e0',
                color: '#000',
              }}
            >
              {c.alias ?? c.field}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.path}>
            {columns.map((c) => (
              <td
                key={c.field}
                style={{
                  borderBottom: '1px solid #ddd',
                  padding: '3px 6px',
                  verticalAlign: 'top',
                  color: '#000',
                }}
              >
                <CellView cell={row.cells[c.field]} onOpenLink={onOpenLink} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DataviewList({
  rows,
  onOpenLink,
}: {
  rows: { path: string; cells: Record<string, RunnerCell> }[];
  onOpenLink?: (path: string, modifier: 'pane-a' | 'pane-b') => void;
}) {
  return (
    <ul style={{ margin: 0, paddingLeft: 18 }}>
      {rows.map((row) => (
        <li key={row.path} style={{ color: '#000' }}>
          <CellView cell={row.cells['file.link']} onOpenLink={onOpenLink} />
        </li>
      ))}
    </ul>
  );
}

function CellView({
  cell,
  onOpenLink,
}: {
  cell: RunnerCell | undefined;
  onOpenLink?: (path: string, modifier: 'pane-a' | 'pane-b') => void;
}) {
  if (!cell || cell.kind === 'empty') return <span style={{ color: '#888' }}>—</span>;
  if (cell.kind === 'text') return <span>{cell.value}</span>;
  if (cell.kind === 'number') return <span>{cell.value}</span>;
  if (cell.kind === 'boolean') return <span>{cell.value ? 'true' : 'false'}</span>;
  if (cell.kind === 'date') return <span title={cell.iso}>{cell.display}</span>;
  if (cell.kind === 'tags') {
    return (
      <span>
        {cell.tags.map((t, i) => (
          <span
            key={t}
            style={{
              background: '#e6e6f0',
              border: '1px solid #c0c0d8',
              padding: '0 4px',
              marginRight: i === cell.tags.length - 1 ? 0 : 4,
              borderRadius: 2,
              fontSize: 11,
            }}
          >
            #{t}
          </span>
        ))}
      </span>
    );
  }
  if (cell.kind === 'link') {
    return (
      <a
        href={`#${cell.path}`}
        onClick={(e) => {
          if (!onOpenLink) return;
          e.preventDefault();
          const split = e.metaKey || e.ctrlKey || e.shiftKey;
          onOpenLink(cell.path, split ? 'pane-b' : 'pane-a');
        }}
        style={{
          color: '#0033a0',
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        {cell.label}
      </a>
    );
  }
  return null;
}
